/* eslint-disable no-param-reassign */
/* eslint-disable no-plusplus */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React from "react"
import { sanitize } from "dompurify"
import { isNull, isUndefined } from "lodash"
import { format } from "date-fns"
import fr from "date-fns/locale/fr"
import it from "date-fns/locale/it"
import i18next from "i18next"
import ValidatorsUtils from "./validators"
import { getCurrencySymbol } from "./currency"
import StoreService from "../services/store"

class Utils {
    /**
     * sluggify Transforme une chaine de caractaire "testString" en "test-string"
     * et enlève tous les caractaires non pris en charges
     * @param s chaine à transformer
     */
    public static sluggify(s: string): string {
        let result = s
        result = result.replace(
            /[A-Z]/g,
            (letter) => `-${letter.toLowerCase()}`
        )
        result = result.replace(/^\s+|\s+$/g, "") // trim
        result = result.toLowerCase()

        const from = "àáäâèéëêìíïîòóöôùúüûñç·/_,:;"
        const to = "aaaaeeeeiiiioooouuuunc------"
        for (let i = 0, l = from.length; i < l; i += 1) {
            result = result.replace(
                new RegExp(from.charAt(i), "g"),
                to.charAt(i)
            )
        }

        return result
            .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
            .replace(/\s+/g, "-") // collapse whitespace and replace by -
            .replace(/-+/g, "-") // collapse dashes
    }

    /**
     * toClassName Transforme une chaine de caractaire "test-string" en "testString"
     * @param s chaine à transformer
     */
    public static toClassName(s: string): string {
        return s.replace(
            /-[a-z0-9]/g,
            (letter: string) => `${letter.replaceAll("-", "").toUpperCase()}`
        )
    }

    /**
     * Returns a random integer between min (inclusive) and max (inclusive).
     * The value is no lower than min (or the next integer greater than min
     * if min isn't an integer) and no greater than max (or the next integer
     * lower than max if max isn't an integer).
     * Using Math.round() will give you a non-uniform distribution!
     */
    public static getRandomInt(min: number, max: number): number {
        const minCeil = Math.ceil(min)
        const maxFloor = Math.floor(max)
        return Math.floor(Math.random() * (maxFloor - minCeil + 1)) + minCeil
    }

    public static nullUndefinedToEmptyString(input: string): string {
        if (isNull(input) || isUndefined(input)) return ""
        return input
    }

    /**
     * Fait un format de la chaine qui est passé en id
     * @param pipe nom du pipe à appliquer
     * @param data data sur laquel appliquer le pipe
     * @param key clef d'où la data vient
     * @param context context d'où la data vient
     */
    public static specialTypeRender = (
        pipe: string | undefined,
        data: any,
        key?: string,
        context?: any
    ): any => {
        if (
            (pipe === "miniDate" ||
                pipe === "shortDate" ||
                pipe === "transactionDate" ||
                pipe === "dateTime" ||
                pipe === "date" ||
                pipe === "longDate" ||
                pipe === "longDateTime") &&
            (isNull(data) || isUndefined(data) || data === "")
        ) {
            return ""
        }
        if (pipe === "capitalize") {
            if (typeof data === "string") {
                return data
                    .split(" ")
                    .map((word: string) => {
                        return word.charAt(0).toUpperCase() + word.slice(1)
                    })
                    .join(" ")
            }
            return data
        }
        if (pipe === "capitalizeFirstLetter") {
            if (typeof data === "string") {
                return data.charAt(0).toUpperCase() + data.slice(1)
            }
            return data
        }
        if (pipe === "miniDate" && ValidatorsUtils.isDate(data)) {
            return format(new Date(data), "d/MM", {
                locale: context?.lang === "it" ? it : fr,
            })
        }
        if (pipe === "shortDate" && ValidatorsUtils.isDate(data)) {
            return format(new Date(data), "EEE d/MM", {
                locale: context?.lang === "it" ? it : fr,
            })
        }
        if (pipe === "transactionDate" && ValidatorsUtils.isDate(data)) {
            return format(new Date(data), "eee dd/MM HH:mm", {
                locale: context?.lang === "it" ? it : fr,
            })
        }
        if (pipe === "dateTime" && ValidatorsUtils.isDate(data)) {
            return format(new Date(data), "dd/MM/yyyy - HH:mm", {
                locale: context?.lang === "it" ? it : fr,
            })
        }
        if (pipe === "date" && ValidatorsUtils.isDate(data)) {
            return format(new Date(data), "dd/MM/yyyy", {
                locale: context?.lang === "it" ? it : fr,
            })
        }
        if (pipe === "longDate" && ValidatorsUtils.isDate(data)) {
            return format(new Date(data), "eee dd MMM yyyy", {
                locale: context?.lang === "it" ? it : fr,
            })
        }
        if (pipe === "longDateTime" && ValidatorsUtils.isDate(data)) {
            return format(new Date(data), "eee dd MMM yyyy HH:mm", {
                locale: context?.lang === "it" ? it : fr,
            })
        }
        if (pipe === "uppercase" && typeof data?.toUpperCase === "function") {
            return data.toUpperCase()
        }
        if (pipe === "pin") {
            if (!data) return i18next.t("No")
            if (data) return i18next.t("Yes")
        }
        if (pipe === "currency") {
            const stringValue = data.toString()
            if (stringValue !== "0") {
                // Set the separator
                const separator = ","
                // Ensure the string has at least 3 characters for decimals (i.e. 001 for 1 cent)
                const paddedValue = stringValue.padStart(3, "0")

                // positionement de la virgule (ex. 123 -> 1.23)
                const position = paddedValue.length - 2
                // Combine parts of the string with the separator
                const integerPart = paddedValue.slice(0, position)
                const decimalPart = paddedValue.slice(position)
                // Ajout des espaces comme séparateurs de milliers sur la partie entière
                const formattedInt = integerPart.replace(
                    /\B(?=(\d{3})+(?!\d))/g,
                    " "
                )
                const output = `${formattedInt}${separator}${decimalPart}`
                return output[0] === "," ? `0${output}` : output
            }
            // Return the default 0.00 for zero value
            return "0,00"
        }
        if (pipe === "currencyWithSign") {
            const storeEntity =
                StoreService.getInstance().local.get("currentEntity")

            const currencyCode =
                context?.entity?.settings?.currency ||
                storeEntity?.settings?.currency 
            const currencySymbol = getCurrencySymbol(currencyCode)
            if (typeof data === "string" || typeof data === "number") {
                // conversion en string
                const stringValue = `${data}`

                if (stringValue !== "0" || stringValue.length > 0) {
                    // set separator
                    const separator = ","
                    // pad with zeros if the length is less than 2
                    const paddedValue = stringValue.padStart(3, "0")
                    // positionement de la virgule (ex. 123 -> 1.23)
                    const position = paddedValue.length - 2
                    // recoller les morceaux en une seule string avec espace milliers
                    const integerPart = paddedValue.slice(0, position)
                    const decimalPart = paddedValue.slice(position)
                    const formattedInt = integerPart.replace(
                        /\B(?=(\d{3})+(?!\d))/g,
                        " "
                    )
                    const output = `${formattedInt}${separator}${decimalPart}`
                    return output[0] === ","
                        ? `0${output} ${currencySymbol}`
                        : `${output} ${currencySymbol}`
                }

                // If stringValue is one character or empty, return "0,0X" or "0,00" accordingly
                return `0,${stringValue || "00"} ${currencySymbol}`
            }
            return `0,00 ${getCurrencySymbol(currencyCode)}`
        }
        if (pipe === "decimalCurrency") {
            if (typeof data === "number" || typeof data === "string") {
                const num =
                    typeof data === "number"
                        ? data
                        : parseFloat(
                              `${data}`.replace(/\s/g, "").replace(",", ".")
                          )
                if (Number.isNaN(num)) return "0,00"
                const [intPart, decPart] = num.toFixed(2).split(".")
                const formattedInt = intPart.replace(
                    /\B(?=(\d{3})+(?!\d))/g,
                    " "
                )
                return `${formattedInt},${decPart}`
            }
            return "0,00"
        }
        if (pipe === "integerWithSpaces") {
            if (typeof data === "number" || typeof data === "string") {
                const raw = `${data}`.trim()
                const sign = raw.startsWith("-") ? "-" : ""
                const digits = raw.replace(/[^0-9]/g, "")
                if (digits.length === 0) return "0"
                const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                return `${sign}${formatted}`
            }
            return "0"
        }
        if (pipe === "rateWithSign") {
            if (data === null) return ` ~ %`
            if (typeof data === "string" || typeof data === "number") {
                // conversion en string
                let stringValue = `${data}`
                let sign = "+"
                if (stringValue.charAt(0) === "-") {
                    stringValue = stringValue.replace("-", "")
                    sign = "-"
                }
                if (stringValue !== "0" || stringValue.length > 0) {
                    // set separator
                    const separator = ","
                    // pad with zeros if the length is less than 2
                    const paddedValue = stringValue.padStart(3, "0")
                    // positionement de la virgule (ex. 123 -> 1.23)
                    const position = paddedValue.length - 2
                    // recoller les morceaux en une seule string
                    const output = [
                        paddedValue.slice(0, position),
                        separator,
                        paddedValue.slice(position),
                    ].join("")
                    if (output == "0,00") return ` ${output} %`
                    else
                        return output[0] === ","
                            ? `${sign} 0${output} %`
                            : `${sign} ${output} %`
                }

                // If stringValue is one character or empty, return "0,0X" or "0,00" accordingly
                return `0,${stringValue || "00"} %`
            }
            return `0,00 %`
        }
        if (pipe === "transactionType") {
            if (key && key === "VAD") return "VAD"
            switch (data) {
                case "Debit":
                    return i18next.t("debit")
                case "Refund":
                    return i18next.t("refund")
                case "Reversal":
                    return i18next.t("reversal")
                case "InitialReservation":
                    return i18next.t("initialReservation")
                case "ClosingPayment":
                    return i18next.t("closingPayment")
                default:
                    return data
            }
        }
        if (pipe === "notificationStatus") {
            switch (data) {
                case "none":
                    return i18next.t("created")
                case "orderSent":
                    return i18next.t("orderSent")
                case "paid":
                    return i18next.t("paid")
                default:
                    return data
            }
        }
        if (pipe === "status") {
            switch (data) {
                case "active":
                    return i18next.t("active")
                case "inactive":
                    return i18next.t("inactive")
                case "free":
                    return i18next.t("free")
                case "used":
                    return i18next.t("used")
                case "blocked":
                    return i18next.t("blocked")
                default:
                    return data
            }
        }
        if (pipe === "resaStatus") {
            switch (data) {
                case "booked":
                    return i18next.t("reservation")
                case "renting":
                    return i18next.t("renting")
                case "pending":
                    return i18next.t("prebooking")
                case "validate":
                    return i18next.t("Confirmed")
                case "canceled":
                    return i18next.t("Cancelled")
                case "finished":
                    return i18next.t("finished")
                case "archived":
                    return i18next.t("archived")
                default:
                    return data
            }
        }
        if (pipe === "resaPaymentStatus") {
            switch (data) {
                case "fullyPaid":
                    return i18next.t("pay_total")
                case "deposit":
                    return i18next.t("pay_partial")
                case "none":
                    return i18next.t("pay_none")
                default:
                    return data
            }
        }
        if (pipe === "resaSource") {
            switch (data) {
                case "web":
                    return i18next.t("web")
                case "store":
                    return i18next.t("store")
                default:
                    return data
            }
        }
        if (pipe === "translation") {
            return i18next.t(data)
        }
        if (pipe === "highlight" && key && context) {
            // on cherche si le champ est disponible
            if (context[`__highlight__${key}`])
                return React.createElement("span", {
                    dangerouslySetInnerHTML: {
                        __html: sanitize(context[`__highlight__${key}`]),
                    },
                })
        }
        if (pipe === "highlightCurrency" && key && context) {
            if (context[`__highlight__${key}`]) {
                let html = context[`__highlight__${key}`]
                let index = html.length
                let intCount = 0
                let res = html
                let padding = ""
                let dataLength = context[key].toString().length

                if (context[key] && (dataLength === 1 || dataLength === 2)) {
                    padding = dataLength === 1 ? "0,0" : "0,"
                }

                while (index > 0 && intCount < 2) {
                    if (!isNaN(parseInt(html[index - 1]))) intCount += 1
                    index -= 1
                }
                // ajout ,
                res =
                    res.slice(0, index) +
                    (padding !== "" ? padding : ",") +
                    res.slice(index)
                // ajout euro
                res += ` ${getCurrencySymbol(
                    context?.currency || context?.entity?.settings?.currency
                )}`

                return React.createElement("span", {
                    dangerouslySetInnerHTML: {
                        __html: "<strong>" + sanitize(res) + "</strong>",
                    },
                })
            } else {
                return React.createElement("span", {
                    dangerouslySetInnerHTML: {
                        __html:
                            "<strong>" +
                            sanitize(
                                Utils.specialTypeRender(
                                    "currencyWithSign",
                                    data,
                                    key,
                                    context
                                )
                            ) +
                            "</strong>",
                    },
                })
            }
        }
        if (pipe === "contractDuration") {
            return `${data} mois`
        }
        if (pipe === "contractDurationSubscription") {
            return `Abonnement ${data} mois`
        }
        if (pipe === "ref") {
            return `Ref. ${data}`
        }
        if (pipe === "contractStatus") {
            switch (data) {
                case "renewable":
                    return "A renouveler"
                case "filing":
                    return "En cours de saisie"
                case "review":
                    return "Attente de contrôle"
                case "approved":
                    return "Approuvé"
                case "refused":
                    return "Action demandée"
                case "pending":
                    return "Attente paiement"
                case "paid":
                    return "Réglé"
                case "expired":
                    return "Expiré"
                case "archived":
                    return "Archivé"
                case "active":
                    return "En cours"
                case "N/A":
                    return "Non créé"
                default:
                    return ""
            }
        }
        if (pipe === "percent") {
            const str = typeof data === "string" ? data : data.toString()
            return str.replace(".", ",") + " %"
        }

        if (pipe === "initials") {
            const arr = typeof data === "string" ? data.split(" ") : ""
            if (arr.length > 1) {
                if (arr[1].charAt(0) === "(") {
                    return (data[0].charAt(0) + data[1].charAt(0)).toUpperCase()
                }
                return (arr[0].charAt(0) + arr[1].charAt(0)).toUpperCase()
            } else {
                if (data.length >= 2)
                    return (data[0].charAt(0) + data[1].charAt(0)).toUpperCase()
                return arr[0].charAt(0).toUpperCase()
            }
        }

        return i18next.t(data)
    }

    /**
     * Parses a given input to a number. If the input is a string, it attempts to parse it as a float.
     * If the input is already a number, it returns the number. If the input cannot be parsed to a number,
     * it returns 0.
     *
     * @param input - The input value to be parsed, which can be of any type.
     * @returns The parsed number, or 0 if the input cannot be parsed to a number.
     */
    public static parseStringOrNumberToNumber(input: any): number {
        if (typeof input === "string") {
            const parsed = parseFloat(input)
            return Number.isNaN(parsed) ? 0 : parsed
        }
        return typeof input === "number" ? input : 0
    }

    /**
     * Fait un format des tableaux de prix
     * format respecté de la bd
     * @param array le tableau à formater
     * @param mod un boolean pour formater tout le tab ou juste la moitié
     */
    public static formatPriceArrays = (
        array: Array<any>,
        mod?: boolean
    ): Array<any> => {
        for (let index = 0; index < array.length; index++) {
            if (mod && index % 2 === 0) {
                array[index] = parseFloat(array[index])
            } else {
                array[index] = parseFloat(
                    this.currencyToDatabaseFormat(array[index])
                )
            }
        }
        return array
    }

    /**
     * currencyToDatabaseFormat  repositionement de la virgule (ex. 1.25 -> 123)
     */
    public static currencyToDatabaseFormat(data: string | number): string {
        const number: number =
            typeof data === "string"
                ? parseFloat(data.replace(",", "."))
                : (data as number)
        if (Number.isInteger(data)) {
            return `${number.toFixed()}00`
        }
        return Number.isNaN(number) ? "000" : number.toFixed(2).replace(".", "")
    }

    /**
     * decimalCheck vérifier si la chaine passée est un decimal (avec . ou , )
     */
    public static decimalCheck(data: string): boolean {
        const regexDecimal = /^[0-9,.]*$/
        if (typeof data === "string" && data.match(regexDecimal)) {
            return true
        }
        return false
    }

    /**
     * Fait un format de la valeur
     * @param value valeur de slider
     */
    public static formatLabelHours = (value: number, index: number): string => {
        if (value % 1 === 0) return `${value}:00`
        if (value % 1 <= 0.25) return `${Math.floor(value)}:15`
        else if (value % 1 > 0.25 && value % 1 <= 0.5)
            return `${Math.floor(value)}:30`
        else if (value % 1 > 0.5 && value % 1 < 1)
            return `${Math.floor(value)}:45`
        else return `${value}:00`
    }

    public static renderDays = (number: number): string => {
        switch (number) {
            case 0:
                return i18next.t("days.sunday")
            case 1:
                return i18next.t("days.monday")
            case 2:
                return i18next.t("days.tuesday")
            case 3:
                return i18next.t("days.wednesday")
            case 4:
                return i18next.t("days.thursday")
            case 5:
                return i18next.t("days.friday")
            case 6:
                return i18next.t("days.saturday")

            default:
                return ""
        }
    }

    public static formatTime = (number: number): string => {
        if (number.toString().length === 2) return `${number.toString()}:00`
        if (number.toString().length === 1) return `0${number.toString()}:00`
        if (number.toString().length > 2) {
            const arr = number.toString().split(".")
            let hour =
                arr[0].length === 1
                    ? `0${arr[0].toString()}`
                    : `${arr[0].toString()}`
            if (number % 1 <= 0.25) return `${hour}:15`
            if (number % 1 > 0.25 && number % 1 <= 0.5) return `${hour}:30`
            if (number % 1 > 0.5 && number % 1 < 1) return `${hour}:45`
        }
        return number.toString()
    }

    public static formatTimeDate = (number: number): string => {
        if (number.toString().length === 2) return number.toString()
        return `0${number.toString()}`
    }
}

export default Utils
